import { useQuery } from '@tanstack/react-query';
import api from '../utils/api';

export interface PartnerApiKey {
  apiKeyId: number;
  partnerName: string;
  keyPrefix: string;
  environment: 'live' | 'test';
  scopes: string[];
  status: string;
  rateTier?: string;
  baaExecuted: boolean;
  baaEffectiveDate: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
  tenant: { companyId: number | null; distributorId: number | null; role: string };
  billing: {
    platformFeeMonthly: number;
    monthlyEligibilityQuota: number;
    eligibilityUsed: number;
    quotaPeriod: string;
    hardCap: boolean;
  };
  usage: { ivrSubmissionsThisMonth: number; eligibilityChecksThisMonth: number };
}

interface PartnerKeyResponse {
  test: PartnerApiKey | null;
  live: PartnerApiKey | null;
  /** Back-compat alias (live ?? test). */
  partner: PartnerApiKey | null;
}

/**
 * Resolves BOTH the test and live partner API keys for the logged-in user's OWN
 * tenant (the backend scopes it strictly to req.user). Each is null when the
 * tenant has no key of that environment. The portal nav uses `hasKey` to decide
 * whether to surface the "API" item; the API page renders both. `partner` is the
 * back-compat alias (live ?? test). Cached so the per-layout nav check is cheap.
 */
export function usePartnerApi() {
  const q = useQuery<PartnerKeyResponse>({
    queryKey: ['partner-portal-key'],
    queryFn: async () => {
      const res = await api.get('/api/partner-portal/key');
      const data = res.data?.data ?? {};
      const test = (data.test ?? null) as PartnerApiKey | null;
      const live = (data.live ?? null) as PartnerApiKey | null;
      return { test, live, partner: (data.partner ?? live ?? test ?? null) as PartnerApiKey | null };
    },
    staleTime: 60_000,
    retry: false,
  });
  const test = q.data?.test ?? null;
  const live = q.data?.live ?? null;
  return {
    test,
    live,
    partner: q.data?.partner ?? live ?? test ?? null,
    hasKey: !!(test || live),
    loading: q.isLoading,
    refetch: q.refetch,
  };
}
